import axios from 'axios';
import { kml } from '@tmcw/togeojson';
import { getKmlClickDetails } from './kml-feature-details';

export const convertKmlToGeoJson = (source) => {
    const document = new DOMParser().parseFromString(source, 'application/xml');
    if (document.getElementsByTagName('parsererror').length
        || document.documentElement.localName !== 'kml') {
        throw new Error('The overlay is not a valid KML document.');
    }
    if (document.getElementsByTagNameNS('*', 'NetworkLink').length) {
        throw new Error('KML network links are not supported by the GeoJSON layer.');
    }
    const geoJson = kml(document);
    const sources = ['Placemark', 'GroundOverlay'].flatMap(name => Array.from(document.getElementsByTagNameNS('*', name)));
    const features = geoJson.features.map((feature, index) => ({
        ...feature,
        properties: {
            ...feature.properties,
            kmlDetails: sources[index] ? getKmlClickDetails(sources[index]) : undefined,
        },
    }));
    if (!features.some(feature => feature.geometry)) {
        throw new Error('The KML overlay contains no supported geometry.');
    }
    return { ...geoJson, features };
};

export const loadKmlGeoJson = async (url, signal) => {
    // Overlay URLs are external; never attach Fleet API credentials.
    const response = await axios.get(url, {
        signal,
        withCredentials: false,
        responseType: 'text',
        // Keep the raw string; axios would otherwise try to JSON.parse it.
        transformResponse: data => data,
    }).catch((error) => {
        // Aborts are expected when the overlay is toggled off, so only real failures are logged.
        if (!axios.isCancel(error)) {
            /* eslint-disable-next-line no-console */
            console.error('KML download failed:', url, error.response?.status ?? error.message);
        }
        return null;
    });
    return response ? convertKmlToGeoJson(response.data) : null;
};

const loadIcon = (url, cache) => {
    if (!cache.has(url)) {
        cache.set(url, new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                if (!image.naturalWidth || !image.naturalHeight) {
                    reject(new Error(`KML icon has invalid dimensions: ${url}`));
                    return;
                }
                resolve({ width: image.naturalWidth, height: image.naturalHeight });
            };
            // Resolve null instead of rejecting so the layer still renders with fallback icon
            image.onerror = () => resolve(null);
            image.src = url;
        }));
    }
    return cache.get(url);
};

export const getKmlFeatureStyle = async (feature, sourceUrl, iconDimensions = new Map()) => {
    const style = { clickable: true, strokeColor: '#0000FF', strokeOpacity: 0.30, strokeWeight: 4, fillColor: '#0000FF', fillOpacity: 0.10 };
    const selectedStyle = feature.getProperty('kmlStyles');
    const getProperty = property => selectedStyle?.[property] ?? feature.getProperty(property);

    const properties = {
        stroke: 'strokeColor',
        'stroke-opacity': 'strokeOpacity',
        'stroke-width': 'strokeWeight',
        fill: 'fillColor',
        'fill-opacity': 'fillOpacity',
    };

    Object.entries(properties).forEach(([property, styleKey]) => {
        const value = getProperty(property);
        if (value !== undefined) style[styleKey] = value;
    });

    const geometryType = feature.getGeometry()?.getType();
    const isPointGeometry = geometryType === 'Point'
        || geometryType === 'MultiPoint';

    const icon = getProperty('icon')
        || (isPointGeometry
            ? 'https://maps.google.com/mapfiles/kml/paddle/blu-blank.png'
            : undefined);
    if (icon) {
        const url = new URL(icon, sourceUrl);
        if (['http:', 'https:'].includes(url.protocol)) {
            const dimensions = await loadIcon(url.href, iconDimensions);
            if (dimensions) {
                const { width, height } = dimensions;
                const ratio = 32 / Math.max(width, height);
                // Google's KmlLayer anchors every icon at its bottom center and ignores <hotSpot>.
                const anchorX = width / 2;
                const anchorY = height;

                style.icon = {
                    url: url.href,
                    scaledSize: new window.google.maps.Size(width * ratio, height * ratio),
                    anchor: new window.google.maps.Point(anchorX * ratio, anchorY * ratio),
                };
            }
        }
    }
    return style;
};
